import { z } from 'zod';
import { PAYMENT_METHODS, RECURRING_FREQUENCIES } from '../enums';
import { zDateOnly, zId, zIdList, zIso, zOptId, zOptText, zPositiveCents, zText } from './common';

export const fundSchema = z.object({
  name: zText(80),
  code: zOptText(20),
  campusId: zOptId,
  isDefault: z.boolean().default(false),
  isTaxDeductible: z.boolean().default(true),
  visibility: z.enum(['everywhere', 'staff_only']).default('everywhere'),
  isActive: z.boolean().default(true),
  sortOrder: z.number().int().min(0).max(999).default(0),
});

export const batchSchema = z.object({ campusId: zOptId, description: zText(120) });

export const designationSchema = z.object({ fundId: zId, amountCents: zPositiveCents });

export const donationSchema = z
  .object({
    personId: zOptId,
    campusId: zOptId,
    batchId: zOptId,
    receivedAt: zIso,
    paymentMethod: z.enum(PAYMENT_METHODS),
    checkNumber: zOptText(30),
    designations: z.array(designationSchema).min(1, 'Agrega al menos un fondo').max(20),
    feeCents: z.number().int().min(0).max(100_000_00).default(0),
    note: zOptText(500),
  })
  .refine((d) => d.paymentMethod !== 'check' || !!d.checkNumber, {
    message: 'Número de cheque requerido',
    path: ['checkNumber'],
  })
  .refine((d) => new Set(d.designations.map((x) => x.fundId)).size === d.designations.length, {
    message: 'Cada fondo solo puede aparecer una vez',
    path: ['designations'],
  });

export const refundSchema = z.object({ amountCents: zPositiveCents, reason: zOptText(300) });

export const recurringDonationSchema = z.object({
  personId: zId,
  fundId: zId,
  amountCents: zPositiveCents,
  frequency: z.enum(RECURRING_FREQUENCIES),
  nextPaymentOn: zDateOnly,
});

export const recurringUpdateSchema = z.object({
  amountCents: zPositiveCents.optional(),
  frequency: z.enum(RECURRING_FREQUENCIES).optional(),
  nextPaymentOn: zDateOnly.optional(),
  status: z.enum(['active', 'paused', 'cancelled']).optional(),
});

export const pledgeCampaignSchema = z
  .object({
    name: zText(120),
    campusId: zOptId,
    fundId: zId,
    startsOn: zDateOnly,
    endsOn: zDateOnly,
    goalCents: zPositiveCents.nullable().optional(),
  })
  .refine((c) => c.endsOn >= c.startsOn, { message: 'El fin debe ser posterior al inicio', path: ['endsOn'] });

export const pledgeSchema = z.object({ personId: zId, amountCents: zPositiveCents });

export const statementRequestSchema = z.object({
  year: z.number().int().min(2000).max(2100),
  personIds: zIdList.optional(),
  send: z.boolean().default(false),
});

/** Métodos de pago en línea: tarjeta o cuenta bancaria de EE. UU. (ACH). */
export const ONLINE_METHODS = ['card', 'us_bank_account'] as const;
export type OnlineMethod = (typeof ONLINE_METHODS)[number];

/** Donación única desde el portal de miembros. */
export const onlineGiftSchema = z.object({
  fundId: zId,
  amountCents: zPositiveCents,
  coverFees: z.boolean().default(false),
  method: z.enum(ONLINE_METHODS).default('card'),
});

/** Donación recurrente desde el portal: se guarda el método en Stripe y se cobra en cada fecha. */
export const onlineRecurringSchema = z.object({
  fundId: zId,
  amountCents: zPositiveCents,
  coverFees: z.boolean().default(false),
  method: z.enum(ONLINE_METHODS).default('card'),
  frequency: z.enum(RECURRING_FREQUENCIES),
  startOn: zDateOnly,
});

/** Lo que la persona puede cambiar de su donación recurrente. */
export const myRecurringUpdateSchema = z.object({
  amountCents: zPositiveCents.optional(),
  frequency: z.enum(RECURRING_FREQUENCIES).optional(),
  nextPaymentOn: zDateOnly.optional(),
  status: z.enum(['active', 'paused', 'cancelled']).optional(),
});

export const donationFilterSchema = z.object({
  personId: zId.optional(),
  fundId: zId.optional(),
  campusId: zId.optional(),
  batchId: zId.optional(),
  from: zDateOnly.optional(),
  to: zDateOnly.optional(),
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(200).default(50),
});
